import { useState, useEffect } from 'react';
import { apiRequest } from '@/lib/queryClient';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { Badge } from '@shared/components/ui/badge';
import { Edit, Check, Building, Wrench, Package } from 'lucide-react';

interface ReviewStepProps {
  data: any;
  onEditStep: (stepIndex: number) => void;
}

export default function ReviewStep({ data, onEditStep }: ReviewStepProps) {
  const [defaults, setDefaults] = useState<any>(null);
  const [templates, setTemplates] = useState<any[]>([]);
  const [businessTypes, setBusinessTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Load defaults, templates, and business types
  useEffect(() => {
    async function loadData() {
      try {
        // Load business types
        const businessTypesRes = await apiRequest('GET', '/api/v1/onboarding/business-types');
        const businessTypesData = await businessTypesRes.json();
        const businessTypesArray = businessTypesData.businessTypes || businessTypesData;
        setBusinessTypes(Array.isArray(businessTypesArray) ? businessTypesArray : []);

        // Load defaults
        const defaultsRes = await apiRequest('GET', `/api/v1/onboarding/defaults/${data.businessType}`);
        const defaultsData = await defaultsRes.json();
        setDefaults(defaultsData);

        // Load templates
        const templatesRes = await apiRequest('GET', `/api/v1/onboarding/templates/${data.businessType}`);
        const templatesData = await templatesRes.json();
        const templatesArray = templatesData.templates || templatesData;
        setTemplates(Array.isArray(templatesArray) ? templatesArray : []);
      } catch (err) {
        console.error('Failed to load review data:', err);
      } finally {
        setLoading(false);
      }
    }

    if (data.businessType) {
      loadData();
    }
  }, [data.businessType]);
  const businessInfo = {
    businessType: data.businessType || '',
    shopName: data.shopName || '',
    phone: data.phone || '',
    email: data.email || '',
    address: data.address || {},
    defaultShopRate: data.defaultShopRate || 0,
    taxRate: data.taxRate || 0,
    currency: data.currency || 'USD',
    shopHours: data.shopHours || {}
  };

  const serviceConfig = {
    serviceTypes: data.serviceTypes || [],
    serviceCategories: data.serviceCategories || [],
    serviceRequestStatuses: data.serviceRequestStatuses || [],
    selectedTemplates: data.selectedTemplates || [],
    templateOverrides: data.templateOverrides || {}
  };

  const inventoryConfig = {
    categories: data.categories || [],
    assetTypes: data.assetTypes || []
  };

  const formatAddress = (address: any) => {
    if (!address || typeof address !== 'object') return 'Not provided';
    const parts = [address.street, address.city, address.state, address.zipCode].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Not provided';
  };

  const formatShopHours = (hours: any) => {
    if (!hours || typeof hours !== 'object') return 'Not configured';
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const formatted = days.map(day => {
      const dayHours = hours[day];
      if (!dayHours || !dayHours.open || !dayHours.close) return null;
      return `${day.charAt(0).toUpperCase() + day.slice(1)}: ${dayHours.open} - ${dayHours.close}`;
    }).filter(Boolean);
    return formatted.length > 0 ? formatted.join(', ') : 'Not configured';
  };

  // Helper functions to get human-readable labels
  const getServiceTypeLabel = (value: string) => {
    if (!defaults?.serviceTypes) return value;
    const serviceType = defaults.serviceTypes.find((item: any) => 
      (typeof item === 'string' ? item : item.value) === value
    );
    return (typeof serviceType === 'object' && serviceType?.label) || value;
  };

  const getServiceCategoryLabel = (value: string) => {
    if (!defaults?.serviceCategories) return value;
    const serviceCategory = defaults.serviceCategories.find((item: any) => 
      (typeof item === 'string' ? item : item.value) === value
    );
    return (typeof serviceCategory === 'object' && serviceCategory?.label) || value;
  };

  const getBusinessTypeLabel = (value: string) => {
    if (!businessTypes || businessTypes.length === 0) return value;
    const businessType = businessTypes.find((item: any) => item.value === value);
    return businessType?.label || value;
  };

  if (loading) {
    return <div className="p-8 text-center">Loading review data...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Review Your Setup</h2>
        <p className="text-muted-foreground">
          Please review all the information below. You can edit any section by clicking the &quot;Edit&quot; button.
        </p>
      </div>

      {/* Business Information */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building className="h-5 w-5 text-primary" />
              <CardTitle>Business Information</CardTitle>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEditStep(0)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Business Type</label>
              <p className="font-medium">{getBusinessTypeLabel(businessInfo.businessType) || 'Not provided'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Shop Name</label>
              <p className="font-medium">{businessInfo.shopName || 'Not provided'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Phone</label>
              <p className="font-medium">{businessInfo.phone || 'Not provided'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Email</label>
              <p className="font-medium">{businessInfo.email || 'Not provided'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Address</label>
              <p className="font-medium">{formatAddress(businessInfo.address)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Default Shop Rate</label>
              <p className="font-medium">${businessInfo.defaultShopRate}/hour</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Tax Rate</label>
              <p className="font-medium">{businessInfo.taxRate}%</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Currency</label>
              <p className="font-medium">{businessInfo.currency}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Shop Hours</label>
              <p className="font-medium text-sm">{formatShopHours(businessInfo.shopHours)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Service Configuration */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-primary" />
              <CardTitle>Service Configuration</CardTitle>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEditStep(1)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Service Types & Categories - Side by Side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-2 block">Service Types</label>
              <div className="flex flex-wrap gap-1">
                {serviceConfig.serviceTypes.length > 0 ? (
                  serviceConfig.serviceTypes.map((type: any, index: number) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {getServiceTypeLabel(typeof type === 'string' ? type : type.value || type.label)}
                    </Badge>
                  ))
                ) : (
                  <span className="text-muted-foreground text-sm">None configured</span>
                )}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground mb-2 block">Service Categories</label>
              <div className="flex flex-wrap gap-1">
                {serviceConfig.serviceCategories.length > 0 ? (
                  serviceConfig.serviceCategories.map((category: any, index: number) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {getServiceCategoryLabel(typeof category === 'string' ? category : category.value || category.label)}
                    </Badge>
                  ))
                ) : (
                  <span className="text-muted-foreground text-sm">None configured</span>
                )}
              </div>
            </div>
          </div>

          {/* Service Request Statuses */}
          <div>
            <label className="text-sm font-medium text-muted-foreground mb-2 block">Service Request Statuses</label>
            <div className="flex flex-wrap gap-1">
              {serviceConfig.serviceRequestStatuses.length > 0 ? (
                serviceConfig.serviceRequestStatuses.map((status: any, index: number) => {
                  const statusLabel = typeof status === 'string' ? status : status.label || status.value;
                  
                  // Look up the color from defaults
                  let statusColor = '#6b7280'; // default gray
                  if (defaults?.serviceRequestStatuses) {
                    const defaultStatus = defaults.serviceRequestStatuses.find((item: any) => {
                      if (typeof item === 'string') {
                        return item === statusLabel;
                      }
                      // Check both label and value for matching
                      return (item.label === statusLabel) || (item.value === statusLabel);
                    });
                    if (typeof defaultStatus === 'object' && defaultStatus?.color) {
                      statusColor = defaultStatus.color;
                    }
                  }
                  
                  return (
                    <Badge 
                      key={index} 
                      variant="secondary"
                      className="text-xs"
                      style={{ backgroundColor: statusColor, color: 'white' }}
                    >
                      {statusLabel}
                    </Badge>
                  );
                })
              ) : (
                <span className="text-muted-foreground text-sm">None configured</span>
              )}
            </div>
          </div>

          {/* Service Templates */}
          <div>
            <label className="text-sm font-medium text-muted-foreground mb-2 block">Service Templates</label>
            <div className="space-y-2">
              {serviceConfig.selectedTemplates.length > 0 ? (
                <div className="space-y-1">
                  {serviceConfig.selectedTemplates.map((templateId: string) => {
                    // Find the base template in the templates array
                    const baseTemplate = templates?.find(t => t.id === templateId);
                    // Check if there are overrides for this template
                    const templateOverride = serviceConfig.templateOverrides[templateId];
                    
                    // Merge base template with overrides, giving priority to overrides
                    const displayTemplate = baseTemplate ? {
                      ...baseTemplate,
                      ...templateOverride
                    } : templateOverride;
                    
                    if (!displayTemplate) return null;
                    
                    return (
                      <div key={templateId} className="flex items-center gap-2 text-sm">
                        <div className="w-2 h-2 rounded-full bg-primary"></div>
                        <span className="font-medium">{displayTemplate.name}</span>
                        {templateId.startsWith('temp_') && (
                          <Badge variant="outline" className="text-xs">New</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <span className="text-muted-foreground text-sm">No templates selected</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Inventory Configuration */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              <CardTitle>Inventory Configuration</CardTitle>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEditStep(2)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium text-muted-foreground">Asset Categories</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {inventoryConfig.categories.length > 0 ? (
                inventoryConfig.categories.map((category: any, index: number) => (
                  <Badge key={index} variant="secondary">{category.name}</Badge>
                ))
              ) : (
                <p className="text-muted-foreground">No asset categories configured</p>
              )}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">Asset Types</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {inventoryConfig.assetTypes.length > 0 ? (
                inventoryConfig.assetTypes.map((type: any, index: number) => (
                  <Badge key={index} variant="secondary">{type.name}</Badge>
                ))
              ) : (
                <p className="text-muted-foreground">No asset types configured</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <Card className="bg-green-50 border-green-200">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-green-900">Setup Complete</h3>
              <p className="text-sm text-green-700">
                Your shop is ready to go! Click &quot;Complete Setup&quot; to finish the onboarding process.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}